import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {osName} from 'react-device-detect';
import {getGqlErrorMessage} from '@/api/request';
import {useGA4WithParams} from '@/hooks/tracking';
import {MobileAnalysisItem} from '@/map/@types';
import {
  convertToVarsFromMobileVars,
  useQueryBasicAnalysis,
  useQueryRegionByPoint,
} from '@/map/api';
import {
  mapAgentTypeAtom,
  mobileAnalysisActiveTabAtom,
  mobileAnalysisPointAtom,
  selectedNearbyInfoItemCardAtom,
  selectedNearbyStoreCategoryAtom,
  selectedNearbyStoreItemCardAtom,
} from '@/map/atom';
import {AnalysisTabType, GeoDataType} from '@/map/enums';
import {BottomSheet} from '../BottomSheet';
import {NearbyInfoCards, StoreCards} from '../Card';
import {InternalToaster} from '../InternalToaster';
import {AiReportButton} from './AiReportButton';
import {AnalysisTabs} from './AnalysisTabs';
import {
  AiCommentReport,
  BusSubwayReport,
  ChildcareCentersReport,
  Customer,
  DeliveryReport,
  FootTrafficReport,
  HallSalesReport,
  HospitalReport,
  NearbyStores,
  PopulationReport,
  SchoolReport,
} from './Report';
import {TopSearchBar} from './TopSearchBar';

interface IProps {
  map?: naver.maps.Map;
  item: MobileAnalysisItem;
  onClose(): void;
  onSearchFocus(): void;
}

export const AnalysisDrawer = ({
  map,
  item: itemOrigin,
  onClose,
  onSearchFocus,
}: IProps) => {
  const item = useMemo(() => {
    return convertToVarsFromMobileVars(itemOrigin);
  }, [itemOrigin]);
  const mapAgent = useAtomValue(mapAgentTypeAtom);

  const [activeTab, setActiveTab] = useAtom(mobileAnalysisActiveTabAtom);

  const [selectedNearbyCard, setSelectedNearbyCard] = useAtom(
    selectedNearbyInfoItemCardAtom,
  );
  const [selectedCategory, setSelectedCategory] = useAtom(
    selectedNearbyStoreCategoryAtom,
  );
  const [selectedStoreCard, setSelectedStoreCard] = useAtom(
    selectedNearbyStoreItemCardAtom,
  );

  const {data: pointEmd} = useQueryRegionByPoint(
    !!itemOrigin?.center && {
      lng: itemOrigin.center.x,
      lat: itemOrigin.center.y,
      type: GeoDataType.Emd,
    },
  );

  const {data, isPending, error} = useQueryBasicAnalysis(item as any);
  const errorMessage = !!error ? getGqlErrorMessage(error) : undefined;

  const setAnalysisPoint = useSetAtom(mobileAnalysisPointAtom);

  const [historyStack, setHistoryStack] = useState<AnalysisTabType[]>([]);

  const [hidden, setHide] = useState<boolean>(false);
  const ref = useRef<any>();

  const tracking = useGA4WithParams();

  const onPrev = () => {
    if (selectedStoreCard) {
      setSelectedStoreCard(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    } else if (selectedNearbyCard) {
      setSelectedNearbyCard(null);
    } else if (historyStack?.length) {
      const last = historyStack.slice(-1);
      const nextStack = historyStack.slice(0, historyStack.length - 1);

      setActiveTab(last[0]);
      setHistoryStack(nextStack);
    } else {
      onSearchFocus();
    }
  };

  const onRadiusChanged = (radius: number) => {
    setAnalysisPoint((prev) =>
      prev
        ? {
            ...prev,
            radius,
          }
        : prev,
    );
  };

  const onTabChange = ({id, label}: {id: AnalysisTabType; label: string}) => {
    setActiveTab((currentTab) => {
      setHistoryStack((prev) => [...prev, currentTab]);
      tracking('tab_select', {
        agent: mapAgent,
        tabName: label,
        radius: itemOrigin.radius,
      });
      return id;
    });
  };

  useEffect(() => {
    return () => {
      setActiveTab(AnalysisTabType.basic);
      setSelectedNearbyCard(null);
      setSelectedCategory(null);
      setSelectedStoreCard(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (ref.current) {
      map.removeListener(ref.current);
    }
    ref.current = map.addListener('click', () => {
      setHide((prev) => !prev);
    });

    return () => {
      if (ref.current) {
        map.removeListener(ref.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (pointEmd) {
      tracking('analyisis_circle_quickmenu', {
        osName,
        areaName: pointEmd.name,
        agent: mapAgent,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointEmd]);

  if (!item) {
    return null;
  }

  return (
    <>
      {!!selectedNearbyCard && <NearbyInfoCards hidden={hidden} />}
      {!!selectedStoreCard && <StoreCards hidden={hidden} />}
      <InternalToaster />
      <BottomSheet
        hidden={hidden || !!selectedNearbyCard || !!selectedStoreCard}
        tabs={
          <AnalysisTabs
            activeTab={activeTab}
            radius={itemOrigin.radius}
            onTabChange={onTabChange}
            onChangeRadius={onRadiusChanged}
          />
        }
      >
        {activeTab === AnalysisTabType.ai && (
          <AiCommentReport
            map={map}
            item={item}
            data={data}
            isPending={isPending}
            error={errorMessage}
          />
        )}
        {activeTab === AnalysisTabType.basic && (
          <PopulationReport
            map={map}
            item={item}
            data={data}
            isPending={isPending}
            error={errorMessage}
          />
        )}
        {activeTab === AnalysisTabType.footTraffic && (
          <FootTrafficReport map={map} item={item} />
        )}
        {activeTab === AnalysisTabType.customer && <Customer item={item} />}
        {activeTab === AnalysisTabType.delivery && (
          <DeliveryReport item={item} />
        )}
        {activeTab === AnalysisTabType.hallSales && (
          <HallSalesReport
            item={item}
            data={data?.hallSales}
            isPending={isPending}
            error={errorMessage}
          />
        )}
        {activeTab === AnalysisTabType.station && (
          <BusSubwayReport map={map} item={item} />
        )}
        {activeTab === AnalysisTabType.hospital && (
          <HospitalReport map={map} item={item} />
        )}
        {activeTab === AnalysisTabType.childcare && (
          <ChildcareCentersReport map={map} item={item} />
        )}
        {activeTab === AnalysisTabType.school && (
          <SchoolReport map={map} item={item} />
        )}
        {activeTab === AnalysisTabType.stores && (
          <NearbyStores map={map} item={item} />
        )}
      </BottomSheet>
      {!hidden && !selectedNearbyCard && !selectedStoreCard && (
        <AiReportButton />
      )}
      <TopSearchBar
        hidden={hidden}
        initialValue={itemOrigin.keyword}
        onPrev={onPrev}
        onFocus={onSearchFocus}
        onClose={onClose}
      />
    </>
  );
};
